import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Snowflake, AlertCircle } from "lucide-react";

const AuthCallback = () => {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const session_id = params.get("session_id");
    if (!session_id) { nav("/"); return; }

    const tryAxios = async () => {
      const r = await api.post("/auth/session", { session_id });
      return r.data;
    };

    const tryFetch = async () => {
      // iOS Safari sometimes blocks axios in this scenario; native fetch is more reliable
      const res = await fetch(`${API_BASE}/auth/session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ session_id }),
        cache: "no-store",
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${res.statusText}: ${text.slice(0, 200)}`);
      }
      return res.json();
    };

    (async () => {
      let data = null;
      let firstErr = null;
      try {
        data = await tryAxios();
      } catch (e) {
        firstErr = e;
      }
      if (!data) {
        // small wait + retry with native fetch
        await new Promise((r) => setTimeout(r, 600));
        try {
          data = await tryFetch();
        } catch (e2) {
          const detail =
            firstErr?.response?.data?.detail
            || firstErr?.message
            || e2?.message
            || "Errore sconosciuto";
          setDebugInfo({
            apiBase: API_BASE,
            ua: navigator.userAgent,
            online: navigator.onLine,
            firstErr: firstErr?.message,
            firstErrCode: firstErr?.code,
            firstErrStatus: firstErr?.response?.status,
            secondErr: e2?.message,
          });
          setErrorMsg(detail);
          return;
        }
      }
      setUser(data.user);
      if (data.session_token) {
        localStorage.setItem("session_token", data.session_token);
      }
      window.history.replaceState({}, document.title, "/dashboard");
      window.location.replace("/dashboard");
    })();
  }, [nav, setUser]);

  if (errorMsg) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4" data-testid="auth-callback-error">
        <div className="max-w-md text-center bg-white border border-red-200 rounded-2xl p-8 shadow-sm">
          <div className="w-12 h-12 rounded-xl bg-red-100 text-red-600 grid place-items-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h1 className="font-heading text-xl font-medium text-[#032B44] mb-2">Accesso non riuscito</h1>
          <p className="text-sm text-slate-600 mb-4">{errorMsg}</p>
          {debugInfo && (
            <details className="text-left text-[11px] text-slate-500 bg-slate-50 rounded-lg p-3 mb-4">
              <summary className="cursor-pointer font-medium">Dettagli tecnici</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">{JSON.stringify(debugInfo, null, 2)}</pre>
            </details>
          )}
          <button
            onClick={() => { window.location.href = "/"; }}
            className="h-11 px-6 rounded-lg bg-[#032B44] hover:bg-[#00A3FF] text-white text-sm font-medium transition-colors"
            data-testid="auth-callback-retry"
          >
            Torna alla home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] grid place-items-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-12 h-12 rounded-xl bg-[#032B44] text-white grid place-items-center mx-auto mb-4 animate-pulse">
          <Snowflake className="w-6 h-6" />
        </div>
        <p className="text-sm text-slate-600">Accesso in corso…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
